import { ExerciseArt } from './ExerciseArt';
import type { Challenge } from '../data/types';
import type { ReactNode } from 'react';
import { slotsFor } from '../data/film';

/** What you actually do in an exercise, from its `tag`: the badge a tile wears. */
const ACTIVITY: Record<string, { label: string; icon: ReactNode }> = {
  words: { label: 'Writing', icon: <path d="M4 20l4-1 11-11-3-3L5 16l-1 4zM14 6l3 3" /> },
  ideas: { label: 'Ideas', icon: <><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></> },
  draw: { label: 'Drawing', icon: <><path d="M18 3l3 3-9 9-4 1 1-4 9-9z" /><path d="M3 21c2-1 3-3 5-3" /></> },
  photo: { label: 'Photo', icon: <><rect x="3" y="7" width="18" height="13" rx="3" /><path d="M8 7l2-3h4l2 3" /><circle cx="12" cy="13.5" r="3.5" /></> },
  voice: { label: 'Talking', icon: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></> },
};

/**
 * One exercise as a big tile (K34 N-03): its picture, its title and minutes,
 * and a badge saying what kind of thing you do in it (writing, drawing,
 * talking…). A warm-up, with nothing for the film, says warm-up instead, and
 * a witness exercise (about a real friend, feeds nothing on purpose) says who
 * it is for. The
 * piece of the film it feeds is the section heading's job, not the badge's
 * (owner, 2026-09-22: "→ SOMEONE" on the picture read as noise).
 */
export function ExerciseTile({ c, next, small, onPick }: { c: Challenge; next?: boolean; small?: boolean; onPick: (c: Challenge) => void }) {
  const warmup = slotsFor(c).length === 0;
  const act = ACTIVITY[c.tag];
  return (
    <button type="button" className={`tile ${small ? 'small' : ''} ${next ? 'next' : ''}`} onClick={() => onPick(c)} aria-label={`${c.title}, ${c.minutes} minutes`}>
      <div className="tile-art-wrap"><ExerciseArt id={c.id} className="tile-art" /></div>
      {c.witness ? <span className="tile-piece plain">for a friend</span> : warmup ? <span className="tile-piece plain">warm-up</span> : act && (
        <span className="tile-piece">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{act.icon}</svg>
          {act.label}
        </span>
      )}
      <div className="tile-body">
        <b className="tile-title">{c.title}</b>
        <span className="tile-meta">{next ? 'Next on the road · ' : ''}{c.minutes} min</span>
      </div>
    </button>
  );
}

/**
 * Open play: the blank page, off the road (moved here from the Road, owner
 * 2026-09-29). A quiet row under the film, never the page's main action, so
 * it wears no button colour. "Deal me anything" was taken out the same day.
 */
export function OpenPlay({ onPick }: { onPick: () => void }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="label">Open play</span>
      <button type="button" className="road-row open-play" onClick={onPick}>
        <ExerciseArt id="pages" className="road-img" />
        <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
          <b style={{ fontSize: 15 }}>A blank page</b>
          <span className="hint">Off the road. Nobody reads it, not even your crew.</span>
        </div>
        <span className="chev" aria-hidden>›</span>
      </button>
    </div>
  );
}
